import React, { useState, useEffect, useMemo } from 'react'
import LoggerUI, { addLog } from '../../components/LoggerUI'
import { OTPVerificationFailedCase as OTPFailedCase } from '../../types/otp.types'
import { OTPHeader } from './OTPHeader'
import { OTPInputContainer } from './OTPInputContainer'
import { OTPErrorMessage } from './OTPErrorMessage'
import { OTPResendButton } from './OTPResendButton'
import { OTPLoadingState } from './OTPLoadingState'
import BackButton from '../../components/ui-components/BackButton'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../state-management/contexts/ToastContext'
import { getBottomPadding, getTopPadding } from '../../utils/sessionStorage'
import { useSetAppBarTheme } from '../../hooks/useSetAppBarTheme'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'

interface OTPVerificationPageProps {
  numberOfDigits?: number
  phoneNumber?: string
  emailId?: string
  isOTPVerified: boolean
  OTPVerificationFailedCase: OTPFailedCase | null
  isOTPVerificationLoading: boolean
  isOTPVerificationLimitExceeded: boolean
  handleResendOTP: () => void
  handleOTPVerification: (otp: string) => void
  onBackClick?: () => void
  inputContainerTestLocator?: string
  otpErrorMessage?: string
  trackingFund?: { fundIsin: string; fundName?: string }
}

export const OTPVerficationPage: React.FC<OTPVerificationPageProps> = ({
  numberOfDigits = 6,
  phoneNumber,
  emailId,
  isOTPVerified,
  OTPVerificationFailedCase,
  isOTPVerificationLoading,
  isOTPVerificationLimitExceeded,
  handleResendOTP,
  handleOTPVerification,
  onBackClick,
  inputContainerTestLocator,
  otpErrorMessage,
  trackingFund
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(numberOfDigits).fill(''))
  const [isOTPAutoFilled, setIsOTPAutoFilled] = useState(false)
  const [resendTimer, setResendTimer] = useState(30) // Start with 30 seconds
  const [hideOTPResendText, setHideOTPResendText] = useState(false)
  const [resetFocus, setResetFocus] = useState(false)
  const navigate = useNavigate()

  useSetAppBarTheme({ appBarTheme: 'dark' })

  const { showToast } = useToast()

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'Enter OTP',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  const handleResetFocus = () => {
    setResetFocus(false)
  }

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendTimer])

  // Auto-verify when all digits are entered
  useEffect(() => {
    if (otp.length === numberOfDigits && otp.every((digit) => digit !== '')) {
      addLog(`OTP entered: ${otp.join('')}`, 'success')
      setTimeout(
        () => {
          handleOTPVerification(otp.join(''))
        },
        isOTPAutoFilled ? 1000 : 0
      )
    }
  }, [otp, numberOfDigits])

  const handleOTPChange = (newOtp: string[], isOTPAutoFilled = false) => {
    setOtp(newOtp)
    if (isOTPAutoFilled) {
      setIsOTPAutoFilled(true)
    }
  }

  const clearOTP = () => {
    setOtp(new Array(numberOfDigits).fill(''))
    setResetFocus(true)
  }

  const handleResendClick = () => {
    if (resendTimer === 0 && !isOTPVerificationLimitExceeded) {
      handleResendOTP()
      setResendTimer(30) // Reset timer to 30 seconds
      clearOTP()
      setHideOTPResendText(true)
      const duration = 1500
      showToast({ message: 'OTP resent', duration })
      setTimeout(() => {
        setHideOTPResendText(false)
      }, duration)
      addLog('Resend OTP requested', 'info')
    }
  }

  const handleBackClick = () => {
    // blur any input selected and after 100ms do below action
    if (document && document.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
      ; (document.activeElement as HTMLElement).blur()
    }
    setTimeout(() => {
      if (onBackClick) {
        onBackClick()
      } else {
        navigate(-1)
      }
    }, 250)
  }

  // Determine if there's an error state
  const hasError = useMemo(
    () => OTPVerificationFailedCase !== null || !!otpErrorMessage,
    [OTPVerificationFailedCase, otpErrorMessage]
  )

  useEffect(() => {
    if (hasError) {
      clearOTP()
    }
  }, [hasError])

  useEffect(() => {
    if (isOTPVerified) {
      // clearOTP()
    }
  }, [isOTPVerified])

  const isDisabled = useMemo(
    () => isOTPVerificationLoading || isOTPVerificationLimitExceeded,
    [isOTPVerificationLoading, isOTPVerificationLimitExceeded]
  )

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      {/* Main Content */}
      <div style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }} className='flex-1 flex px-5'>
        <div className='w-full max-w-sm flex flex-col'>
          <div className='flex-shrink-0'>
            <BackButton size={24} onClick={handleBackClick} />
          </div>

          <div className='flex flex-shrink-0 pt-8'>
            {/* Header */}
            <OTPHeader phoneNumber={phoneNumber || ''} emailId={''} />
          </div>

          {/* OTP Input Container */}
          <div className='pb-6 flex flex-1 overflow-y-auto'>
            <div className='flex flex-col'>
              <div>
                <OTPInputContainer
                  testLocator={inputContainerTestLocator}
                  numberOfDigits={numberOfDigits}
                  otp={otp}
                  onOTPChange={handleOTPChange}
                  isError={hasError}
                  isDisabled={isDisabled}
                  resetFocus={resetFocus}
                  handleResetFocus={handleResetFocus}
                />
              </div>

              {/* Error Message */}
              <OTPErrorMessage
                hasError={hasError}
                OTPVerificationFailedCase={OTPVerificationFailedCase}
                otpErrorMessage={otpErrorMessage}
              />
              <OTPLoadingState
                isOTPVerificationLoading={isOTPVerificationLoading}
                isOTPVerficationSuccess={isOTPVerified}
              />
            </div>
          </div>

          <div style={{ paddingBottom: `calc(${getBottomPadding()}px + 24px)` }} className='flex-shrink-0'>
            {/* Resend Button */}
            {isOTPVerificationLoading ? null : (
              <OTPResendButton
                resendTimer={resendTimer}
                isOTPVerificationLimitExceeded={isOTPVerificationLimitExceeded}
                isOTPVerificationLoading={isOTPVerificationLoading}
                hideOTPResendText={hideOTPResendText}
                onResendClick={handleResendClick}
              />
            )}
          </div>
        </div>
      </div>

      {/* Logger UI */}
      {/* <LoggerUI /> */}
    </div>
  )
}
